import React, { useState, useCallback, useEffect, useRef } from 'react';
import { InputField } from '../ui/InputField';
import {
  MapPin, Navigation, Clock, CreditCard, ChevronDown, Users,
  Briefcase, Calendar, FlipVertical as SwapVertical, User,
  Phone, Mail, Plus, Minus, X, Crosshair, Car
} from 'lucide-react';
import Select from '../ui/Select';
import SectionHeader from '../ui/SectionHeader';
import CountryDropdown from '../CountryDropdown';
import AddressHistoryDropdown from './AddressHistoryDropdown';
import AddressSearchDialog from '../AddressSearchDialog';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import Cookies from 'js-cookie';
import { GOOGLE_MAPS_API_KEY, DEFAULT_MAX_DISTANCE_KM, countryCodes, rideOptions, paymentOptions } from '../../utils/constants';
import { BookingFormProps, BookingData, FormMode } from './types';
import { bookingService } from '../../services/graphql/bookingService';
import { getCurrentUser, logout, isAuthenticated } from '../../services/graphql/authService';
import {
  getLocalAddressHistory,
  saveLocalAddressHistory,
  getLocalBookingDetails,
  saveLocalBookingDetails,
} from '../../services/localStorageService';
import { useServiceKey } from '../../hooks/useServiceKey';
import { useGoogleMaps } from '../../components/GoogleMapsProvider'; // Import from GoogleMapsProvider instead
import { useIsMobile } from '../../hooks/useIsMobile';

import ProgressDialog from '../ProgressDialog';
import GoogleMapComponent from '../GoogleMapComponent/GoogleMapComponent';
import ServiceInfo from '../ServiceInfo';
import Authentication from '../Authentication';
import '../../styles/shared.css'; // Import shared styles
import { trackFormInteraction } from '../../services/analytics';
import logger from '../../services/logger';

interface RouteCoords {
  pickupLatLng: google.maps.LatLngLiteral;
  destLatLng: google.maps.LatLngLiteral;
}

const BookingForm: React.FC<BookingFormProps> = ({
  onShowBookingSummary,
  initialBookingData,
  onDirectionsChange,
  onCenterChange,
  onZoomChange,
  onPickupCoordsChange,
  onDestinationCoordsChange,
  initialMode = 'new',
  currentUser,
  onPickupAddressChange,
  onDestinationAddressChange,
  mapLoadError: externalMapLoadError,
}) => {
  // Use context's isLoaded for Google Maps + Places readiness  
  const { isLoaded: isGoogleMapsLoaded, loadError: internalMapLoadError, maps: mapsInstance } = useGoogleMaps();
  const { serviceConfig } = useServiceKey();
  const maxDistanceKm = serviceConfig.maxDistance || DEFAULT_MAX_DISTANCE_KM;

  // Authentication state
  const [authLoading, setAuthLoading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [center, setCenter] = useState(serviceConfig.defaultLocation || { lat: 40.7128, lng: -74.0060 });
  const zoom = serviceConfig.defaultZoom || 12;

  const [addressHistory, setAddressHistory] = useState<Array<{ id: string; pickup: string; destination: string }>>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showAddressHistory, setShowAddressHistory] = useState(false);

  const [pickupType, setPickupType] = useState<'asap' | 'scheduled'>('asap');
  const [now, setNow] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSelectingFromHistoryRef = useRef(false);
  const hasInitialLoadCompleted = useRef(false);

  const [pickupValue, setPickupValue] = useState(initialBookingData?.pickup || '');
  const [destValue, setDestValue] = useState(initialBookingData?.destination || '');

  useEffect(() => {
    if (typeof onPickupAddressChange === 'function') {
      onPickupAddressChange(pickupValue);
    }
  }, [pickupValue, onPickupAddressChange]);

  useEffect(() => {
    if (typeof onDestinationAddressChange === 'function') {
      onDestinationAddressChange(destValue);
    }
  }, [destValue, onDestinationAddressChange]);

  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined;
    if (pickupType === 'asap') {
      setNow(new Date());
      timerId = setInterval(() => {
        setNow(new Date());
      }, 60000);
    }
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [pickupType]);

  useEffect(() => {
    const loadAddressHistory = async () => {
      setIsLoadingHistory(true);
      try {
        if (currentUser) {
          // Logged-in user: load from server
          const { error, data } = await bookingService.getUserAddressHistory();
          if (error) {
            logger.error('Error loading address history', error instanceof Error ? error : undefined, { error });
          } else if (data && data.length > 0) {
            const uniquePairs = filterUniqueAddressPairs(data);
            setAddressHistory(uniquePairs);
            setShowAddressHistory(uniquePairs.length > 0);
          } else {
            setShowAddressHistory(false);
          }
        } else {
          // Anonymous user: load from localStorage
          const localHistory = getLocalAddressHistory();
          if (localHistory.length > 0) {
            const formattedHistory = localHistory.map(h => ({
              id: h.id,
              pickup: h.pickup,
              destination: h.destination,
            }));
            setAddressHistory(formattedHistory);
            setShowAddressHistory(true);
          } else {
            setAddressHistory([]);
            setShowAddressHistory(false);
          }
        }
      } catch (err) {
        logger.error('Exception loading address history', err instanceof Error ? err : undefined);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadAddressHistory();
  }, [currentUser]);

  const filterUniqueAddressPairs = (addresses: Array<{ id: string; pickup: string; destination: string }>) => {
    const uniquePairs = new Map<string, { id: string; pickup: string; destination: string }>();

    addresses.forEach(address => {
      const pairKey = `${address.pickup}|${address.destination}`;
      if (!uniquePairs.has(pairKey)) {
        uniquePairs.set(pairKey, address);
      }
    });

    return Array.from(uniquePairs.values());
  };

  const today = now.toISOString().split('T')[0];
  const currentTime = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  const [pickup, setPickup] = useState('');
  const [destination, setDestination] = useState('');
  const [selectedRide, setSelectedRide] = useState('economy');
  const [passengers, setPassengers] = useState(1);
  const [luggage, setLuggage] = useState(0);
  const [pickupDate, setPickupDate] = useState(today);
  const [pickupTime, setPickupTime] = useState(currentTime);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [passengerName, setPassengerName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [passengerMobile, setPassengerMobile] = useState('');
  const [passengerEmail, setPassengerEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [routeError, setRouteError] = useState('');
  const [pickupCoords, setPickupCoords] = useState<google.maps.LatLngLiteral | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [communicationPrefs, setCommunicationPrefs] = useState({
    voice: false,
    sms: false,
    whatsapp: false,
    email: true,
  });
  const [communicationError, setCommunicationError] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState<{ car: number; bus: number } | null>(null);
  const [dateTimeError, setDateTimeError] = useState(''); const [isValidRoute, setIsValidRoute] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [sendEmailCopy, setSendEmailCopy] = useState(false);
  const [emailCopyAddress, setEmailCopyAddress] = useState('');
  const [emailCopyError, setEmailCopyError] = useState('');  // Define a new state for handling submission errors from parent component
  const [submissionError, setSubmissionError] = useState<string | null>(null); const [mapLoadError, setMapLoadError] = useState<Error | null>(null);
  // Remove local isMapLoaded, use isGoogleMapsLoaded from context
  const [formErrors, setFormErrors] = useState<string[]>([]); const [formMode, setFormMode] = useState<FormMode>(initialMode);
  const [focusedInput, setFocusedInput] = useState<'pickup' | 'destination' | null>(null);
  const [destCoords, setDestCoords] = useState<google.maps.LatLngLiteral | null>(null);
  // Mobile address search dialog states
  const isMobile = useIsMobile();
  const [addressDialogType, setAddressDialogType] = useState<'pickup' | 'destination' | null>(null);
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  // Form section collapsed state
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);

  const authFormRef = useRef<HTMLDivElement>(null); // Added ref for auth form

  // Handle logout
  const handleLogout = async () => {
    setAuthLoading(true);
    try {
      await logout();
      // Reload page to refresh auth state
      window.location.reload();
    } catch (err) {
      setAuthError(`Error during logout: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setAuthLoading(false);
    }
  };

  const enableGlobalSearchFromConfig = serviceConfig.attributes?.enableGlobalSearch === true;
  useEffect(() => {
    // Combine external and internal map load errors
    if (externalMapLoadError || internalMapLoadError) {
      setMapLoadError(externalMapLoadError || internalMapLoadError || null);
    } else {
      setMapLoadError(null);
    }
  }, [externalMapLoadError, internalMapLoadError]);

  // Close country dropdown when clicking outside of it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (isCountryDropdownOpen) {
        const target = event.target as HTMLElement;
        // Check if the click is outside the dropdown container
        const dropdownElement = document.querySelector('.country-dropdown-container');
        const triggerElement = document.querySelector('.country-dropdown-trigger');

        if (dropdownElement &&
          !dropdownElement.contains(target) &&
          triggerElement &&
          !triggerElement.contains(target)) {
          setIsCountryDropdownOpen(false);
        }
      }
    }

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCountryDropdownOpen]);

  useEffect(() => {
    if (!pickup) {
      setPickupCoords(null);
      onPickupCoordsChange?.(null);
    }
  }, [pickup, onPickupCoordsChange]);

  useEffect(() => {
    if (!destination) {
      setDestCoords(null);
      onDestinationCoordsChange?.(null);
    }
  }, [destination, onDestinationCoordsChange]);

  // No need for local isMapLoaded effect; rely on isGoogleMapsLoaded from context

  useEffect(() => {
    setFormMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    const shouldDefaultMap = (!pickup && !destination) || (!pickupCoords && !destCoords);
    if (shouldDefaultMap) {
      if (directions) {
        onDirectionsChange?.(null);
        setDirections(null);
      }
      setIsValidRoute(false);
      setRouteError('');
      setPickupCoords(null);
      onPickupCoordsChange?.(null);
      setDestCoords(null);
      onDestinationCoordsChange?.(null);
      if (serviceConfig.defaultLocation) {
        const newCenter = { ...serviceConfig.defaultLocation };
        setCenter(newCenter);
        onCenterChange?.(newCenter, false);
        if (serviceConfig.defaultZoom) {
          onZoomChange?.(serviceConfig.defaultZoom);
        } else {
          onZoomChange?.(10);
        }
      } else {
        onCenterChange?.({ lat: 40.7128, lng: -74.0060 });
        onZoomChange?.(2);
      }
    }
  }, [pickup, destination, pickupCoords, destCoords, serviceConfig.defaultLocation, serviceConfig.defaultZoom, directions, onDirectionsChange, onPickupCoordsChange, onDestinationCoordsChange, onCenterChange, onZoomChange, setCenter, setDirections, setIsValidRoute, setRouteError, setPickupCoords, setDestCoords]);

  useEffect(() => {
    if (isValidRoute && directions && directions.routes.length > 0) {
      const legs = directions.routes[0].legs;
      if (legs.length > 0) {
        const destLocation = legs[0].end_location;
        setDestCoords({
          lat: destLocation.lat(),
          lng: destLocation.lng()
        });
      }
    }
  }, [isValidRoute, directions]);

  useEffect(() => {
    if (pickupType === 'asap') {
      setPickupTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }));
    }
  }, [pickupType, now]);

  useEffect(() => {
    if (initialBookingData) {
      setPickup(initialBookingData.pickup);
      setPickupValue(initialBookingData.pickup);
      if (setPickupInputValue) {
        setPickupInputValue(initialBookingData.pickup);
      }

      setDestination(initialBookingData.destination);
      setDestValue(initialBookingData.destination);
      if (setDestInputValue) {
        setDestInputValue(initialBookingData.destination);
      }

      setSelectedRide(initialBookingData.selectedRide);
      setPassengers(initialBookingData.passengers);
      setLuggage(initialBookingData.luggage);
      setPickupDate(initialBookingData.pickupDate);
      setPickupTime(initialBookingData.pickupTime);
      setPickupType(initialBookingData.pickupType);
      setPassengerName(initialBookingData.passengerName);
      setCompanyName(initialBookingData.companyName);
      setCountryCode(initialBookingData.countryCode);
      setPassengerMobile(initialBookingData.passengerMobile);
      setPassengerEmail(initialBookingData.passengerEmail);
      setPaymentMethod(initialBookingData.paymentMethod);
      setCommunicationPrefs(initialBookingData.communicationPrefs);
      setSendEmailCopy(initialBookingData.sendEmailCopy);
      setEmailCopyAddress(initialBookingData.emailCopyAddress);
      setEstimatedDuration(initialBookingData.estimatedDuration);
      setIsValidRoute(true);

      const countryMatch = countryCodes.find(c => c.code === initialBookingData.countryCode);
      if (countryMatch) {
        setSelectedCountry(countryMatch);
      }
    }
  }, [initialBookingData]);
  useEffect(() => {
    if (!initialBookingData) {
      // For logged-in users, prioritize their profile data
      if (currentUser) {
        logger.debug('Loading from logged-in user profile');

        // Use user's name from profile
        if (currentUser.name) {
          logger.debug('Using logged in user name', { name: currentUser.name });
          setPassengerName(currentUser.name);
        }

        // Use user's email from profile
        if (currentUser.email) {
          logger.debug('Using logged in user email', { email: currentUser.email });
          setPassengerEmail(currentUser.email);
        }

        // Parse phone number from profile to extract country code
        if (currentUser.phone) {
          const fullPhone = currentUser.phone;
          logger.debug('Parsing phone from profile', { phone: fullPhone });

          // Sort country codes by length (longest first) to match correctly
          const sortedCountryCodes = [...countryCodes].sort(
            (a, b) => b.code.length - a.code.length
          );

          let matchedCountry = countryCodes[0]; // Default
          let phoneWithoutCode = fullPhone;

          for (const country of sortedCountryCodes) {
            if (fullPhone.startsWith(country.code)) {
              matchedCountry = country;
              phoneWithoutCode = fullPhone.slice(country.code.length);
              break;
            }
          }

          logger.debug('Extracted country and phone', { country: matchedCountry.country, code: matchedCountry.code, phone: phoneWithoutCode });
          setCountryCode(matchedCountry.code);
          setSelectedCountry(matchedCountry);
          setPassengerMobile(phoneWithoutCode);
        }

        // Load other preferences from cookies (these are booking-specific, not profile data)
        const savedPaymentMethod = Cookies.get('paymentMethod');
        const savedCommunicationPrefs = Cookies.get('communicationPrefs');

        if (savedPaymentMethod) {
          setPaymentMethod(savedPaymentMethod);
        }
        if (savedCommunicationPrefs) {
          try {
            setCommunicationPrefs(JSON.parse(savedCommunicationPrefs));
          } catch (e) {
            logger.error('Error parsing saved communication preferences', e instanceof Error ? e : undefined);
          }
        }
      } else if (!isAuthenticated()) {
        // Anonymous user (no auth token): load from cookies and localStorage
        // Skip this if there's an auth token but currentUser hasn't loaded yet (auth in progress)
        logger.debug('Loading saved form values for anonymous user');

        const savedName = Cookies.get('passengerName');
        const savedCompany = Cookies.get('companyName');
        const savedMobile = Cookies.get('passengerMobile');
        const savedCountryCode = Cookies.get('countryCode');
        const savedEmail = Cookies.get('passengerEmail');
        const savedPickup = Cookies.get('pickupLocation');
        const savedDestination = Cookies.get('destinationLocation');
        const savedPassengers = Cookies.get('passengers');
        const savedLuggage = Cookies.get('luggage');
        const savedPaymentMethod = Cookies.get('paymentMethod');
        const savedCommunicationPrefs = Cookies.get('communicationPrefs');

        // Also try localStorage (longer persistence)
        const localBookingDetails = getLocalBookingDetails();

        // Use cookies first, then localStorage as fallback
        const effectiveName = savedName || localBookingDetails?.passengerName;
        const effectiveCompany = savedCompany || localBookingDetails?.companyName;
        const effectiveMobile = savedMobile || localBookingDetails?.passengerMobile;
        const effectiveCountryCode = savedCountryCode || localBookingDetails?.countryCode;
        const effectiveEmail = savedEmail || localBookingDetails?.passengerEmail;
        const effectivePaymentMethod = savedPaymentMethod || localBookingDetails?.paymentMethod;

        if (effectiveName) {
          logger.debug('Found passenger name', { name: effectiveName });
          setPassengerName(effectiveName);
        }
        if (effectiveCompany) {
          logger.debug('Found company name', { company: effectiveCompany });
          setCompanyName(effectiveCompany);
        }
        if (effectiveMobile) {
          logger.debug('Found mobile', { mobile: effectiveMobile });
          setPassengerMobile(effectiveMobile);
        }
        if (effectiveEmail) {
          logger.debug('Found email', { email: effectiveEmail });
          setPassengerEmail(effectiveEmail);
        }

        if (savedPickup) {
          logger.debug('Found pickup location', { pickup: savedPickup });
          setPickup(savedPickup);
          setPickupValue(savedPickup);
          if (setPickupInputValue) setPickupInputValue(savedPickup);
        }
        if (savedDestination) {
          logger.debug('Found destination location', { destination: savedDestination });
          setDestination(savedDestination);
          setDestValue(savedDestination);
          if (setDestInputValue) setDestInputValue(savedDestination);
        }

        // Passengers: load from cookie or use default (1)
        let parsedPassengers = 1;
        if (savedPassengers) {
          const n = parseInt(savedPassengers, 10);
          if (!isNaN(n) && n >= 1 && n <= 6) {
            parsedPassengers = n;
          }
        }
        setPassengers(parsedPassengers);
        logger.debug('Passengers set', { passengers: parsedPassengers });

        // Luggage: load from cookie or use default (0)
        let parsedLuggage = 0;
        if (savedLuggage) {
          const n = parseInt(savedLuggage, 10);
          if (!isNaN(n) && n >= 0 && n <= 3) {
            parsedLuggage = n;
          }
        }
        setLuggage(parsedLuggage);
        logger.debug('Luggage set', { luggage: parsedLuggage });

        if (effectivePaymentMethod) {
          logger.debug('Found payment method', { paymentMethod: effectivePaymentMethod });
          setPaymentMethod(effectivePaymentMethod);
        }

        // Communication prefs from cookies or localStorage
        if (savedCommunicationPrefs) {
          try {
            const prefs = JSON.parse(savedCommunicationPrefs);
            logger.debug('Found communication preferences', { prefs: savedCommunicationPrefs });
            setCommunicationPrefs(prefs);
          } catch (e) {
            logger.error('Error parsing saved communication preferences', e instanceof Error ? e : undefined);
          }
        } else if (localBookingDetails?.communicationPrefs) {
          logger.debug('Found communication preferences from localStorage');
          setCommunicationPrefs(localBookingDetails.communicationPrefs);
        }

        if (effectiveCountryCode) {
          logger.debug('Found country code', { countryCode: effectiveCountryCode });
          setCountryCode(effectiveCountryCode);
          const country = countryCodes.find(c => c.code === effectiveCountryCode);
          if (country) {
            logger.debug('Found matching country', { country: country.country });
            setSelectedCountry(country);
          }
        }
      }
    }
    // Mark that initial load is complete to allow cookie saving
    // Use setTimeout to ensure state updates have been processed before allowing saves
    // This prevents the save effect from running with stale state values
    setTimeout(() => {
      hasInitialLoadCompleted.current = true;
    }, 0);
  }, [initialBookingData, currentUser]);
  // Save pickup to cookie when it changes
  useEffect(() => {
    // Removed saving pickup location to cookie
  }, [pickup, initialBookingData]);
  // Save destination to cookie when it changes
  useEffect(() => {
    // Removed saving destination to cookie
  }, [destination, initialBookingData]);
  // Save passenger name to cookie when it changes
  useEffect(() => {
    if (!initialBookingData && passengerName) {
      logger.debug('Saving passenger name to cookie', { key: 'passengerName', value: passengerName, expires: '30 days' });
      Cookies.set('passengerName', passengerName, { expires: 30 });
    }
  }, [passengerName, initialBookingData]);
  // Save company name to cookie when it changes
  useEffect(() => {
    if (!initialBookingData && companyName) {
      logger.debug('Saving company name to cookie', { key: 'companyName', value: companyName, expires: '30 days' });
      Cookies.set('companyName', companyName, { expires: 30 });
    }
  }, [companyName, initialBookingData]);
  // Save passenger mobile to cookie when it changes
  useEffect(() => {
    if (!initialBookingData && passengerMobile) {
      logger.debug('Saving passenger mobile to cookie', { key: 'passengerMobile', value: passengerMobile, expires: '30 days' });
      Cookies.set('passengerMobile', passengerMobile, { expires: 30 });
    }
  }, [passengerMobile, initialBookingData]);
  // Save country code to cookie and localStorage when it changes
  // Only save after initial load to avoid overwriting previously saved values
  useEffect(() => {
    if (!initialBookingData && countryCode && hasInitialLoadCompleted.current) {
      logger.debug('Saving country code to cookie', { key: 'countryCode', value: countryCode, expires: '30 days' });
      Cookies.set('countryCode', countryCode, { expires: 30 });

      // Also update localStorage for longer persistence (if booking details exist)
      const existingDetails = getLocalBookingDetails();
      if (existingDetails) {
        saveLocalBookingDetails({
          ...existingDetails,
          countryCode,
        });
        logger.debug('Updated country code in localStorage', { countryCode });
      }
    } else {
      logger.debug('Skipped saving country code', { hasInitialBookingData: !!initialBookingData, countryCode, hasInitialLoadCompleted: hasInitialLoadCompleted.current });
    }
  }, [countryCode, initialBookingData]);
  // Save passenger email to cookie when it changes
  useEffect(() => {
    if (!initialBookingData && passengerEmail) {
      logger.debug('Saving passenger email to cookie', { key: 'passengerEmail', value: passengerEmail, expires: '30 days' });
      Cookies.set('passengerEmail', passengerEmail, { expires: 30 });
    }
  }, [passengerEmail, initialBookingData]);  // Save passengers count to cookie when it changes
  useEffect(() => {
    if (!initialBookingData) {
      logger.debug('Saving passengers count to cookie', { key: 'passengers', value: passengers, type: typeof passengers, expires: '30 days' });

      try {
        Cookies.set('passengers', passengers.toString(), { expires: 30 });
        logger.debug('Successfully saved passengers count', { passengers });

        // Verify the cookie was set correctly
        const savedValue = Cookies.get('passengers');
        logger.debug('Verified passengers cookie value', { savedValue });
      } catch (error) {
        logger.error('Failed to save passengers count', error instanceof Error ? error : undefined);
      }
    }
  }, [passengers, initialBookingData]);

  // Save luggage to cookie when it changes
  useEffect(() => {
    if (!initialBookingData) {
      logger.debug('Saving luggage option to cookie', { key: 'luggage', value: luggage, type: typeof luggage, expires: '30 days' });

      try {
        Cookies.set('luggage', luggage.toString(), { expires: 30 });
        logger.debug('Successfully saved luggage option', { luggage });

        // Verify the cookie was set correctly
        const savedValue = Cookies.get('luggage');
        logger.debug('Verified luggage cookie value', { savedValue });
      } catch (error) {
        logger.error('Failed to save luggage option', error instanceof Error ? error : undefined);
      }
    }
  }, [luggage, initialBookingData]);
  // Save payment method to cookie when it changes
  useEffect(() => {
    if (!initialBookingData && paymentMethod) {
      logger.debug('Saving payment method to cookie', { key: 'paymentMethod', value: paymentMethod, expires: '30 days' });
      Cookies.set('paymentMethod', paymentMethod, { expires: 30 });
    }
  }, [paymentMethod, initialBookingData]);
  // Save communication preferences to cookie when they change
  useEffect(() => {
    if (!initialBookingData) {
      const prefsString = JSON.stringify(communicationPrefs);
      logger.debug('Saving communication preferences to cookie', { key: 'communicationPrefs', value: prefsString, expires: '30 days' });
      try {
        Cookies.set('communicationPrefs', prefsString, { expires: 30 });
        logger.debug('Successfully saved communication preferences');
      } catch (error) {
        logger.error('Failed to save communication preferences', error instanceof Error ? error : undefined);
      }
    }
  }, [communicationPrefs, initialBookingData]);

  /**
 * usePlacesAutocomplete should only be initialized with a stable defaultValue ('' or initialBookingData?.pickup/destination).
 * Do NOT pass pickupValue or destValue as defaultValue after mount, as this causes repeated re-initialization.
 * Use setValue to update the input value as the user types.
 */
  const initialPickupDefault = initialBookingData?.pickup || '';
  const initialDestDefault = initialBookingData?.destination || '';

  const {
    ready: pickupReady, // Add ready state
    value: pickupInputValue,
    suggestions: { data: pickupSuggestions, loading: pickupLoading, status: pickupStatus },
    setValue: setPickupInputValue,
    clearSuggestions: clearPickupSuggestions
  } = usePlacesAutocomplete({
    requestOptions: {
      ...((enableGlobalSearchFromConfig || !selectedCountry?.country) ? {} : { componentRestrictions: { country: selectedCountry.country.toLowerCase() } })
    },
    debounce: 300,
    cache: 86400,
    initOnMount: isGoogleMapsLoaded,
    defaultValue: initialPickupDefault, // Only set once
    callbackName: 'pickupSuggestions',
  });

  const {
    ready: destReady, // Add ready state
    value: destInputValue,
    suggestions: { data: destSuggestions, loading: destLoading, status: destStatus },
    setValue: setDestInputValue,
    clearSuggestions: clearDestSuggestions
  } = usePlacesAutocomplete({
    requestOptions: {
      ...((enableGlobalSearchFromConfig || !selectedCountry?.country) ? {} : { componentRestrictions: { country: selectedCountry.country.toLowerCase() } })
    },
    debounce: 300,
    cache: 86400,
    initOnMount: isGoogleMapsLoaded,
    defaultValue: initialDestDefault, // Only set once
    callbackName: 'destSuggestions',
  });
  const forceClearPickupSuggestions = () => {
    clearPickupSuggestions();
    // Triple clear to ensure suggestions are gone
    setTimeout(() => {
      clearPickupSuggestions();
      setTimeout(() => {
        clearPickupSuggestions();
      }, 50);
    }, 50);
  };

  const forceClearDestSuggestions = () => {
    clearDestSuggestions();
    // Triple clear to ensure suggestions are gone
    setTimeout(() => {
      clearDestSuggestions();
      setTimeout(() => {
        clearDestSuggestions();
      }, 50);
    }, 50);
  };// Function to determine if a communication method is available based on service configuration
  const isCommunicationMethodAvailable = (method: string): boolean => {
    // First check the top-level communicationMethods array
    if (serviceConfig.communicationMethods) {
      return serviceConfig.communicationMethods.includes(method);
    }

    // Fallback to check in attributes for backward compatibility
    if (serviceConfig.attributes?.communicationMethods) {
      const configuredMethods = String(serviceConfig.attributes.communicationMethods)
        .toLowerCase()
        .split(',')
        .map(m => m.trim());

      return configuredMethods.includes(method);
    }

    // If no specific methods are configured, all methods are available
    return true;
  };

  useEffect(() => {
    setCommunicationPrefs((previousPrefs) => {
      const nextPrefs = {
        voice: isCommunicationMethodAvailable('voice') ? previousPrefs.voice : false,
        sms: isCommunicationMethodAvailable('sms') ? previousPrefs.sms : false,
        whatsapp: isCommunicationMethodAvailable('whatsapp') ? previousPrefs.whatsapp : false,
        email: isCommunicationMethodAvailable('email') ? previousPrefs.email : false,
      };

      if (!Object.values(nextPrefs).some(Boolean) && isCommunicationMethodAvailable('email')) {
        nextPrefs.email = true;
      }

      return JSON.stringify(previousPrefs) === JSON.stringify(nextPrefs) ? previousPrefs : nextPrefs;
    });
  }, [serviceConfig]);

  const handleCountrySelect = (country: any) => {
    setCountryCode(country.code);
    setSelectedCountry(country);
    setIsCountryDropdownOpen(false);
  };

  const validateEmail = (email: string) => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setPassengerEmail(email);
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleCommunicationPrefChange = (pref: string, checked: boolean) => {
    const newPrefs = { ...communicationPrefs, [pref]: checked };
    const hasSelection = Object.values(newPrefs).some(value => value);

    let currentCommError = '';
    let currentEmailError = emailError;

    if (!hasSelection) {
      currentCommError = 'Please select at least one communication method';
    } else if (newPrefs.email) {
      if (!passengerEmail) {
        currentCommError = 'Email address is required if Email communication is selected';
        currentEmailError = 'Email address is required for communication';
      } else if (!validateEmail(passengerEmail)) {
        currentCommError = 'Please enter a valid email address for communication';
        currentEmailError = 'Please enter a valid email address';
      } else {
        if (currentEmailError === 'Email address is required for communication' || currentEmailError === 'Please enter a valid email address') {
          currentEmailError = '';
        }
      }
    } else {
      if (currentEmailError === 'Email address is required for communication') {
        currentEmailError = '';
      }
    }

    setCommunicationError(currentCommError);
    setEmailError(currentEmailError);
    setCommunicationPrefs(newPrefs);
  };

  const validateDateTime = (date: string, time: string) => {
    if (pickupType === 'asap') {
      setDateTimeError('');
      return true;
    }

    if (!time) {
      setDateTimeError('Please select a pickup time for your scheduled booking');
      return false;
    }

    const selectedDateTime = new Date(`${date}T${time}`);
    const minScheduledDateTime = new Date(new Date().getTime() + 30 * 60000);

    if (selectedDateTime < minScheduledDateTime) {
      setDateTimeError('Scheduled pickup time must be at least 30 minutes from now');
      return false;
    }

    setDateTimeError('');
    return true;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setPickupDate(newDate);
    validateDateTime(newDate, pickupTime);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setPickupTime(newTime);
    validateDateTime(pickupDate, newTime);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}min`;
  };

  const validateAddresses = useCallback(async (pickupAddress: string, destAddress: string): Promise<RouteCoords> => {
    try {
      let pickupGeocode;
      try {
        const pickupResults = await getGeocode({ address: pickupAddress });
        if (!pickupResults || pickupResults.length === 0) {
          throw new Error('Could not find the pickup location. Please provide a complete address including:\n- Street number\n- Street name\n- City\n- State/Province\n- Country\n\nExample: "123 Main Street, New York, NY, USA"');
        }
        pickupGeocode = pickupResults[0];
      } catch (error: any) {
        logger.error('Pickup geocoding error', error instanceof Error ? error : undefined, { status: error?.status });
        setIsValidRoute(false);

        if (error.status === 'ZERO_RESULTS') {
          throw new Error('The pickup location could not be found. Please provide a complete address including:\n- Street number\n- Street name\n- City\n- State/Province\n- Country\n\nExample: "123 Main Street, New York, NY, USA"');
        } else if (error.status === 'OVER_QUERY_LIMIT') {
          throw new Error('Service is temporarily busy. Please try again in a few minutes.');
        } else if (error.status === 'REQUEST_DENIED') {
          throw new Error('Unable to process the pickup address. Please ensure it is a valid street address with all required components.');
        } else if (error.status === 'INVALID_REQUEST') {
          throw new Error('Please enter a complete pickup address with street number, name, city, and country.');
        }
        throw new Error('Unable to validate the pickup address. Please ensure all address components are included and try again.');
      }

      let destGeocode;
      try {
        const destResults = await getGeocode({ address: destAddress });
        if (!destResults || destResults.length === 0) {
          throw new Error('Could not find the destination location. Please provide a complete address including:\n- Street number\n- Street name\n- City\n- State/Province\n- Country\n\nExample: "456 Oak Avenue, Los Angeles, CA, USA"');
        }
        destGeocode = destResults[0];
      } catch (error: any) {
        logger.error('Destination geocoding error', error instanceof Error ? error : undefined, { status: error?.status });
        setIsValidRoute(false);

        if (error.status === 'ZERO_RESULTS') {
          throw new Error('The destination location could not be found. Please provide a complete address including:\n- Street number\n- Street name\n- City\n- State/Province\n- Country\n\nExample: "456 Oak Avenue, Los Angeles, CA, USA"');
        } else if (error.status === 'OVER_QUERY_LIMIT') {
          throw new Error('Service is temporarily busy. Please try again in a few minutes.');
        } else if (error.status === 'REQUEST_DENIED') {
          throw new Error('Unable to process the destination address. Please ensure it is a valid street address with all required components.');
        } else if (error.status === 'INVALID_REQUEST') {
          throw new Error('Please enter a complete destination address with street number, name, city, and country.');
        }
        throw new Error('Unable to validate the destination address. Please ensure all address components are included and try again.');
      }

      const pickupLatLng = await getLatLng(pickupGeocode);
      const destLatLng = await getLatLng(destGeocode);

      const R = 6371;
      const dLat = (destLatLng.lat - pickupLatLng.lat) * Math.PI / 180;
      const dLon = (destLatLng.lng - pickupLatLng.lng) * Math.PI / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(pickupLatLng.lat * Math.PI / 180) * Math.cos(destLatLng.lat * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      if (distance > maxDistanceKm) {
        setIsValidRoute(false);
        throw new Error(`The distance between locations (${Math.round(distance)} km) exceeds our maximum service range of ${maxDistanceKm} km. Please choose a closer destination.`);
      }
      setPickupCoords(pickupLatLng);
      onPickupCoordsChange?.(pickupLatLng);
      setDestCoords(destLatLng);
      onDestinationCoordsChange?.(destLatLng);
      setIsValidRoute(true);
      return { pickupLatLng, destLatLng };
    } catch (error: any) {
      logger.error('Address validation error', error instanceof Error ? error : undefined);
      throw error;
    }
  }, [maxDistanceKm, onPickupCoordsChange, onDestinationCoordsChange]);

  const calculateRoute = useCallback(async (pickupAddressOverride?: string, destinationAddressOverride?: string) => {
    const effectivePickup = pickupAddressOverride || pickup;
    const effectiveDestination = destinationAddressOverride || destination;
    if (!effectivePickup || !effectiveDestination) {
      setRouteError('Please enter both pickup and destination addresses.');
      setIsValidRoute(false);

      setDirections(null);
      onDirectionsChange?.(null);

      if (!effectivePickup && !effectiveDestination && serviceConfig.defaultLocation) {
        const newCenter = { ...serviceConfig.defaultLocation };
        setCenter(newCenter);
        onCenterChange?.(newCenter, false);
        if (serviceConfig.defaultZoom) {
          onZoomChange?.(serviceConfig.defaultZoom);
        } else {
          onZoomChange?.(10);
        }
      }
      return;
    }

    setRouteError('');
    setIsValidRoute(false);

    try {
      const { pickupLatLng, destLatLng } = await validateAddresses(effectivePickup, effectiveDestination);

      setPickupCoords(pickupLatLng);
      onPickupCoordsChange?.(pickupLatLng);
      setDestCoords(destLatLng);
      onDestinationCoordsChange?.(destLatLng);

      if (window.google && window.google.maps) {
        const directionsService = new google.maps.DirectionsService();
        try {
          if (!pickupLatLng || !destLatLng ||
            typeof pickupLatLng.lat !== 'number' ||
            typeof pickupLatLng.lng !== 'number' ||
            typeof destLatLng.lat !== 'number' ||
            typeof destLatLng.lng !== 'number') {
            throw new Error('Invalid coordinates for directions request');
          }

          const result = await directionsService.route({
            origin: pickupLatLng,
            destination: destLatLng,
            travelMode: google.maps.TravelMode.DRIVING,
          });

          logger.debug('Got directions result, updating local state and sending to parent', { result });
          setDirections(result);

          setCenter(pickupLatLng);

          onDirectionsChange?.(result);

          if (!focusedInput) {
            onCenterChange?.(null, true);
          } else {
            onCenterChange?.(pickupLatLng);
          }

          onPickupCoordsChange?.(pickupLatLng);

          const baseDuration = result.routes[0].legs[0].duration!.value / 60;
          setEstimatedDuration({
            car: Math.round(baseDuration),
            bus: Math.round(baseDuration * 1.5),
          });
          setIsValidRoute(true);
        } catch (directionError: any) {
          logger.error('Error getting directions', directionError instanceof Error ? directionError : undefined);
          setRouteError(directionError.message || 'Unable to calculate route. Please check your addresses and try again.');
          setDirections(null);
          onDirectionsChange?.(null);
          setEstimatedDuration(null);
          setIsValidRoute(false);
        }
      } else {
        logger.error('Google Maps API not available');
        setRouteError('Google Maps API is not available. Please refresh the page and try again.');
      }
    } catch (error: any) {
      logger.error('Error calculating route', error instanceof Error ? error : undefined);
      setRouteError(error.message || 'Unable to calculate route. Please check your addresses and try again.');
      setDirections(null);
      setEstimatedDuration(null);
      setIsValidRoute(false);
      onDirectionsChange?.(null);
    }
  }, [pickup, destination, focusedInput, onDirectionsChange, onCenterChange, onPickupCoordsChange, validateAddresses]);

  useEffect(() => {
    if (isGoogleMapsLoaded && pickup && destination && !isSelectingFromHistoryRef.current) {
      calculateRoute();
    }
    if (isSelectingFromHistoryRef.current) {
      isSelectingFromHistoryRef.current = false;
    }
  }, [pickup, destination, calculateRoute, isGoogleMapsLoaded]);
  /**
   * Handles selection of a previous address from history.
   * Ensures auto-suggest dropdowns do NOT open when setting values programmatically.
   * Explicitly closes suggestions after setting values.
   */  const handleAddressHistorySelect = async (selectedPickup: string, selectedDestination: string) => {
    // Set flag to indicate we're selecting from history to prevent auto-suggestions
    isSelectingFromHistoryRef.current = true;

    // First clear suggestions and set focused input to null to prevent dropdowns
    forceClearPickupSuggestions();
    forceClearDestSuggestions();
    setFocusedInput(null);

    // Set values for pickup and destination without triggering suggestions
    setPickupValue(selectedPickup);
    setPickup(selectedPickup);
    setPickupInputValue(selectedPickup, false);
    onPickupAddressChange?.(selectedPickup);

    setDestValue(selectedDestination);
    setDestination(selectedDestination);
    setDestInputValue(selectedDestination, false);
    onDestinationAddressChange?.(selectedDestination);

    // Ensure input fields lose focus to prevent suggestion dropdowns
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Small delay to ensure UI updates, then clear suggestions again in case they appeared
    setTimeout(() => {
      forceClearPickupSuggestions();
      forceClearDestSuggestions();
      setFocusedInput(null);

      // Set a longer timeout to ensure suggestions remain cleared
      setTimeout(() => {
        forceClearPickupSuggestions();
        forceClearDestSuggestions();
        isSelectingFromHistoryRef.current = false; // Reset the flag
      }, 200);
    }, 100);

    // Reset the address history dropdown if present
    const selectElement = document.querySelector('select[data-address-history]') as HTMLSelectElement;
    if (selectElement) {
      selectElement.value = '';
    }

    await calculateRoute(selectedPickup, selectedDestination);
  };

  const handleSwapLocations = () => {
    forceClearPickupSuggestions();
    forceClearDestSuggestions();

    const currentPickup = pickup || pickupValue;
    const currentDestination = destination || destValue;
    const tempPickupValue = currentPickup;
    const tempPickupCoords = pickupCoords;

    setPickupValue(currentDestination);
    setPickupInputValue(currentDestination, false);
    setDestValue(tempPickupValue);
    setDestInputValue(tempPickupValue, false);

    setPickup(currentDestination);
    onPickupAddressChange?.(currentDestination);
    setDestination(currentPickup);
    onDestinationAddressChange?.(currentPickup);

    setPickupCoords(destCoords);
    setDestCoords(tempPickupCoords);
  };

  const clearPickupLocation = () => {
    setPickup('');
    setPickupValue('');
    setPickupInputValue('');
    forceClearPickupSuggestions();
    setPickupCoords(null);
    setIsValidRoute(false);
    onPickupAddressChange?.('');

    if (!destination) {
      logger.debug('Both fields empty, resetting map to default');
      if (serviceConfig.defaultLocation) {
        setDirections(null);
        onDirectionsChange?.(null);

        const newCenter = { ...serviceConfig.defaultLocation };
        setCenter(newCenter);
        onCenterChange?.(newCenter, false);

        if (serviceConfig.defaultZoom) {
          onZoomChange?.(serviceConfig.defaultZoom);
        } else {
          onZoomChange?.(10);
        }
      }
    } else if (destCoords) {
      setCenter(destCoords);
      onCenterChange?.(destCoords);
    }
  };

  const clearDestination = () => {
    setDestination('');
    setDestValue('');
    setDestInputValue('');
    forceClearDestSuggestions();
    setDestCoords(null);
    setIsValidRoute(false);
    onDestinationAddressChange?.('');

    if (!pickup) {
      logger.debug('Both fields empty, resetting map to default');
      if (serviceConfig.defaultLocation) {
        setDirections(null);
        onDirectionsChange?.(null);

        const newCenter = { ...serviceConfig.defaultLocation };
        setCenter(newCenter);
        onCenterChange?.(newCenter, false);

        if (serviceConfig.defaultZoom) {
          onZoomChange?.(serviceConfig.defaultZoom);
        } else {
          onZoomChange?.(10);
        }
      }
    } else if (pickupCoords) {
      setCenter(pickupCoords);
      onCenterChange?.(pickupCoords);
    }
  };
  // Function to handle luggage selection changes
  const handleLuggageChange = (newValue: number) => {
    logger.debug('Changing luggage', { from: luggage, to: newValue });
    setLuggage(newValue);
  };

  const incrementPassengers = () => {
    if (passengers < 6) {
      logger.debug('Incrementing passengers', { from: passengers, to: passengers + 1 });
      const newValue = passengers + 1;
      setPassengers(newValue);
    }
  };

  const decrementPassengers = () => {
    if (passengers > 1) {
      logger.debug('Decrementing passengers', { from: passengers, to: passengers - 1 });
      const newValue = passengers - 1;
      setPassengers(newValue);
    }
  };
  const getCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        const currentCoords = { lat: latitude, lng: longitude };

        try {
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`
          );
          const data = await response.json();
          if (data.results[0]) {
            const address = data.results[0].formatted_address;
            setPickupValue(address);
            setPickup(address);
            setPickupInputValue(address);
            onPickupAddressChange?.(address);
            setPickupCoords(currentCoords);
            setCenter(currentCoords);
            onCenterChange?.(currentCoords);
          }
        } catch (error) {
          logger.error('Error getting address', error instanceof Error ? error : undefined);
        }
      }, (error) => {
        logger.error('Error getting location', error instanceof Error ? error : undefined);
        alert('Unable to get your location. Please ensure location services are enabled.');
      });
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  const validateEmailCopy = (email: string) => {
    if (!email) {
      setEmailCopyError('');
      return true;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    setEmailCopyError(isValid ? '' : 'Please enter a valid email address');
    return isValid;
  };

  const validateForm = () => {
    const errors: string[] = [];
    let currentEmailErr = '';
    let currentCommErr = '';

    if (!pickup) errors.push('Please enter a pickup location');
    if (!destination) errors.push('Please enter a destination');
    if (!isValidRoute) errors.push('Please ensure both addresses are valid and a route can be calculated');
    if (!passengerName) errors.push('Please enter your name');
    if (!passengerMobile) errors.push('Please enter your mobile number');
    if (!paymentMethod) errors.push('Please select a payment method');

    if (!Object.values(communicationPrefs).some(value => value)) {
      errors.push('Please select at least one communication method');
      currentCommErr = 'Please select at least one communication method';
    }
    // Email is required for anonymous bookings (when user is not logged in)
    // This allows us to create an account for them
    if (!currentUser && !passengerEmail) {
      errors.push('Email address is required for booking');
      currentEmailErr = 'Email address is required';
    } else if (!currentUser && passengerEmail && !validateEmail(passengerEmail)) {
      errors.push('Please enter a valid email address');
      currentEmailErr = 'Please enter a valid email address';
    } else if (communicationPrefs.email) {
      if (!passengerEmail) {
        errors.push('Email address is required as per your communication preference');
        currentEmailErr = 'Email address is required for communication';
        if (!currentCommErr) currentCommErr = 'Email address is required for communication';
      } else if (!validateEmail(passengerEmail)) {
        errors.push('Please enter a valid email address for communication');
        currentEmailErr = 'Please enter a valid email address';
        if (!currentCommErr) currentCommErr = 'Invalid email for communication';
      }
    }
    setEmailError(currentEmailErr);
    setCommunicationError(currentCommErr);

    if (sendEmailCopy && emailCopyAddress && !validateEmail(emailCopyAddress)) errors.push('Please enter a valid email address for the booking copy');

    if (pickupType === 'scheduled') {
      if (!pickupTime) {
        errors.push('Please select a pickup time for your scheduled booking');
        setDateTimeError('Please select a pickup time');
      } else if (!validateDateTime(pickupDate, pickupTime)) {
        errors.push(dateTimeError || 'Scheduled pickup time must be at least 30 minutes from now');
      }
    } else {
      const currentAsapTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      if (pickupTime !== currentAsapTime) {
        setPickupTime(currentAsapTime);
      }
      setDateTimeError('');
    }

    setFormErrors(errors);
    return errors.length === 0;
  }; const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formMode === 'readOnly') {
      return;
    }

    if (!validateForm()) {
      trackFormInteraction('BookingForm', 'ValidationFailed');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setSubmissionError(null); // Clear any previous submission errors
    trackFormInteraction('BookingForm', 'SubmitAttempt');

    try {
      Cookies.set('passengerName', passengerName, { expires: 30 });
      Cookies.set('companyName', companyName, { expires: 30 });
      Cookies.set('passengerMobile', passengerMobile, { expires: 30 });
      Cookies.set('countryCode', countryCode, { expires: 30 });
      Cookies.set('passengerEmail', passengerEmail, { expires: 30 });
      // Removed saving pickup and destination to cookie

      const finalPickupTime = pickupType === 'asap'
        ? now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
        : pickupTime;

      const bookingData: BookingData = {
        pickup,
        destination,
        selectedRide,
        passengers,
        luggage,
        pickupDate,
        pickupTime: finalPickupTime,
        pickupType,
        passengerName,
        companyName,
        countryCode,
        passengerMobile,
        passengerEmail,
        paymentMethod,
        communicationPrefs,
        sendEmailCopy,
        emailCopyAddress,
        estimatedDuration
      };

      const bookingRecord = {
        pickup_location: pickup,
        dropoff_location: destination,
        pickup_date: pickupDate,
        pickup_time: finalPickupTime,
        pickup_type: pickupType,
        passenger_name: passengerName,
        company_name: companyName || undefined,
        country_code: countryCode,
        passenger_mobile: passengerMobile,
        passenger_email: passengerEmail || undefined,
        payment_method: paymentMethod,
        communication_prefs: communicationPrefs,
        passengers: passengers,
        luggage: luggage,
        selected_ride: selectedRide,
        estimated_duration: estimatedDuration?.car,
        status: 'pending' as const,
        is_verified: true
      };

      try {
        if (formMode === 'edit' && initialBookingData?.bookingId) {
          const { error, data } = await bookingService.updateBooking(initialBookingData.bookingId, bookingRecord);

          if (error) {
            logger.error('Error updating booking', error instanceof Error ? error : undefined, { message: error?.message });
            setFormErrors(prev => [...prev, `Failed to update booking: ${error.message}`]);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }

          logger.info('Booking updated successfully', { data });
          bookingData.bookingId = initialBookingData.bookingId;
        } else {
          logger.debug('Creating booking with serviceConfig', { serviceConfig });
          const { error, data, isAnonymousBooking, generatedPassword, accountCreated, userEmail } = await bookingService.createBooking(bookingRecord, (serviceConfig as any).serviceId);

          if (error) {
            logger.error('Error saving booking', error instanceof Error ? error : undefined, { message: error?.message });
            setFormErrors(prev => [...prev, `Failed to save booking: ${error.message}`]);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }

          logger.info('Booking saved successfully', { data, isAnonymousBooking, accountCreated });

          if (data && Array.isArray(data) && data.length > 0 && data[0] && typeof data[0].id === 'string' && data[0].id.trim() !== '') {
            bookingData.bookingId = data[0].id;
            if (data[0].booking_number) {
              bookingData.bookingNumber = data[0].booking_number;
            }
          } else {
            logger.error('New booking created but ID is missing or invalid in response data', undefined, { data });
            setFormErrors(prev => [...prev, 'Failed to create booking: Could not retrieve a valid booking ID after creation. Please try again.']);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }

          // Store anonymous booking info for display in summary
          if (isAnonymousBooking) {
            bookingData.isAnonymousBooking = true;
            if (generatedPassword) {
              bookingData.generatedPassword = generatedPassword;
              logger.info('Anonymous booking created with generated password');
            }
          }

          // Save booking details to localStorage for anonymous users (for repeat bookings)
          if (!currentUser) {
            saveLocalBookingDetails({
              passengerName,
              companyName: companyName || undefined,
              countryCode,
              passengerMobile,
              passengerEmail,
              paymentMethod,
              communicationPrefs,
            });
            // Save address pair to local history
            saveLocalAddressHistory(pickup, destination);
            logger.debug('Saved booking details to localStorage for repeat anonymous bookings');
          }
        }        // Call the parent's onShowBookingSummary function
        await onShowBookingSummary({
          ...bookingData,
          companyName,
        });
      } catch (error) {
        logger.error('Exception saving booking', error instanceof Error ? error : undefined);
        setFormErrors(prev => [...prev, `An unexpected error occurred: ${error instanceof Error ? error.message : 'Unknown error'}`]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handlePickupInputChange = (newValue: string) => {
    setPickupValue(newValue); // This updates the state bound to the input

    if (isSelectingFromHistoryRef.current) {
      // Value is being set programmatically from history.
      // Don't fetch suggestions for programmatic changes
      setPickupInputValue(newValue, false);
    } else if (focusedInput === 'pickup') {
      // Only fetch suggestions when input is focused and user is typing
      setPickupInputValue(newValue); // This updates usePlacesAutocomplete, which will fetch suggestions by default
    } else {
      // If not focused, update the value without fetching suggestions
      setPickupInputValue(newValue, false);
      // Ensure suggestions are cleared when not focused
      forceClearPickupSuggestions();
    }

    if (!newValue) {
      setPickup('');
      setPickupCoords(null);
      onPickupCoordsChange?.(null);
      setDirections(null);
      setIsValidRoute(false);
      setRouteError('');
      forceClearPickupSuggestions();
      if (!destination) {
        if (serviceConfig.defaultLocation && serviceConfig.defaultZoom) {
          setCenter(serviceConfig.defaultLocation);
          onCenterChange?.(serviceConfig.defaultLocation);
          onZoomChange?.(serviceConfig.defaultZoom);
        }
        setDirections(null); // Clear directions if pickup is cleared and no destination
        onDirectionsChange?.(null);
      }
    }
  };
  const handleDestinationInputChange = (newValue: string) => {
    setDestValue(newValue);

    if (isSelectingFromHistoryRef.current) {
      // Value is being set programmatically from history.
      // Don't fetch suggestions for programmatic changes
      setDestInputValue(newValue, false);
    } else if (focusedInput === 'destination') {
      // Only fetch suggestions when input is focused and user is typing
      setDestInputValue(newValue); // This will fetch suggestions
    } else {
      // If not focused, update the value without fetching suggestions
      setDestInputValue(newValue, false);
      // Ensure suggestions are cleared when not focused
      forceClearDestSuggestions();
    }

    if (!newValue) {
      setDestination('');
      setDestCoords(null);
      onDestinationCoordsChange?.(null);
      setDirections(null);
      setIsValidRoute(false);
      setRouteError('');
      forceClearDestSuggestions();
      if (!pickup) {
        if (serviceConfig.defaultLocation && serviceConfig.defaultZoom) {
          onCenterChange?.(serviceConfig.defaultLocation);
          onZoomChange?.(serviceConfig.defaultZoom);
        }
        setDirections(null); // Clear directions if destination is cleared and no pickup
        onDirectionsChange?.(null);
      }
    }
  };
  const scrollToAuthForm = () => {
    authFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); // Changed block to 'center'
  };

  // --- Extensive Logging for Pickup/Destination Autosuggest Debugging ---

  useEffect(() => {
    const requestOptions = {
      ...((enableGlobalSearchFromConfig || !selectedCountry?.country) ? {} : { componentRestrictions: { country: selectedCountry.country.toLowerCase() } })
    };
    logger.debug('usePlacesAutocomplete (pickup) actual init params', {
      isGoogleMapsLoaded,
      selectedCountry,
      enableGlobalSearchFromConfig,
      defaultValue: initialPickupDefault,
      initOnMount: isGoogleMapsLoaded,
      apiKeyPresent: !!GOOGLE_MAPS_API_KEY,
      mapsInstanceAvailable: !!mapsInstance,
      internalMapLoadError: internalMapLoadError?.message,
      requestOptions,
    });
  }, [isGoogleMapsLoaded, selectedCountry, enableGlobalSearchFromConfig, initialPickupDefault, mapsInstance, internalMapLoadError]);

  useEffect(() => {
    const requestOptions = {
      ...((enableGlobalSearchFromConfig || !selectedCountry?.country) ? {} : { componentRestrictions: { country: selectedCountry.country.toLowerCase() } })
    };
    logger.debug('usePlacesAutocomplete (destination) actual init params', {
      isGoogleMapsLoaded,
      selectedCountry,
      enableGlobalSearchFromConfig,
      defaultValue: initialDestDefault,
      initOnMount: isGoogleMapsLoaded,
      apiKeyPresent: !!GOOGLE_MAPS_API_KEY,
      mapsInstanceAvailable: !!mapsInstance,
      internalMapLoadError: internalMapLoadError?.message,
      requestOptions,
    });
  }, [isGoogleMapsLoaded, selectedCountry, enableGlobalSearchFromConfig, initialDestDefault, mapsInstance, internalMapLoadError]);

  useEffect(() => {
    logger.debug('pickupInputValue changed', { pickupInputValue });
  }, [pickupInputValue]);

  useEffect(() => {
    logger.debug('destInputValue changed', { destInputValue });
  }, [destInputValue]);

  useEffect(() => {
    logger.debug('pickupSuggestions updated', { suggestions: pickupSuggestions, loading: pickupLoading, status: pickupStatus, ready: pickupReady });
  }, [pickupSuggestions, pickupLoading, pickupStatus, pickupReady]);

  useEffect(() => {
    logger.debug('destSuggestions updated', { suggestions: destSuggestions, loading: destLoading, status: destStatus, ready: destReady });
  }, [destSuggestions, destLoading, destStatus, destReady]);

  useEffect(() => {
    logger.debug('Google Maps load state', { isGoogleMapsLoaded, internalMapLoadError: internalMapLoadError?.message });
  }, [isGoogleMapsLoaded, internalMapLoadError]);
  // Log when user focuses on pickup/destination fields
  const handlePickupFocus = () => {
    if (isMobile && formMode !== 'readOnly') {
      setAddressDialogType('pickup');
      setIsAddressDialogOpen(true);
    } else {
      setFocusedInput('pickup');
      logger.debug('Pickup field focused');
    }
  };

  const commitTypedPickupLocation = () => {
    const value = pickupValue.trim();
    if (value && value !== pickup) {
      setPickup(value);
      onPickupAddressChange?.(value);
    }
  };

  const commitTypedDestination = () => {
    const value = destValue.trim();
    if (value && value !== destination) {
      setDestination(value);
      onDestinationAddressChange?.(value);
    }
  };

  const handleDestinationFocus = () => {
    if (isMobile && formMode !== 'readOnly') {
      setAddressDialogType('destination');
      setIsAddressDialogOpen(true);
    } else {
      setFocusedInput('destination');
      logger.debug('Destination field focused');
    }
  };

  // Handle address selection from mobile dialog
  const handleMobileAddressSelect = (address: string, coordinates?: { lat: number; lng: number }) => {
    if (addressDialogType === 'pickup') {
      setPickupValue(address);
      setPickup(address);
      setPickupInputValue(address, false);
      onPickupAddressChange?.(address);

      if (coordinates) {
        setPickupCoords(coordinates);
        onPickupCoordsChange?.(coordinates);
        setCenter(coordinates);
        onCenterChange?.(coordinates);
      }
    } else if (addressDialogType === 'destination') {
      setDestValue(address);
      setDestination(address);
      setDestInputValue(address, false);
      onDestinationAddressChange?.(address);

      if (coordinates) {
        setDestCoords(coordinates);
        onDestinationCoordsChange?.(coordinates);
        setCenter(coordinates);
        onCenterChange?.(coordinates);
      }
    }

    setIsAddressDialogOpen(false);
    setAddressDialogType(null);
  };

  // Close mobile dialog
  const handleCloseMobileDialog = () => {
    setIsAddressDialogOpen(false);
    setAddressDialogType(null);
  };

  return (
    <div className="flex flex-col xl:flex-row gap-6 lg:gap-8 w-full items-start text-foreground">
      <div className="w-full xl:w-2/5 order-1">
        <ServiceInfo />
        <div className="w-full mb-4" ref={authFormRef}>
          <Authentication
            currentUser={currentUser}
            handleLogout={handleLogout}
            authLoading={authLoading}
            authError={authError}
          />
        </div>
      </div>

      <div className="w-full xl:w-3/5 order-2">
        <div className="card-container">
          <form onSubmit={handleSubmit} className="space-y-6 w-full" data-testid="booking-form">
            {/* Show ProgressDialog when submitting */}
            {isSubmitting && (
              <ProgressDialog isOpen={isSubmitting} message="Submitting your booking..." subMessage="Please wait while we process your request." />
            )}

            <div className="form-container">
              {/* Form header section */}
              <SectionHeader
                title={formMode === 'edit' ? 'Edit Your Booking' : formMode === 'readOnly' ? 'View Booking' : 'Request a Ride'}
                icon={Car}
                collapsible={true}
                collapsed={isFormCollapsed}
                onCollapsedChange={setIsFormCollapsed}
                rightContent={
                  <div className={`text-sm px-3 py-1 rounded-lg font-medium ${formMode === 'new' ? 'bg-success/20 text-success-foreground' :
                    formMode === 'edit' ? 'bg-info/20 text-info-foreground' :
                      'bg-secondary text-secondary-foreground'
                    }`}>
                    {formMode === 'new' ? 'New Booking' :
                      formMode === 'edit' ? 'Edit Mode' :
                        'View Only'}
                  </div>
                }
              >
                {/* Errors and notices section */}
              {(routeError || formErrors.length > 0 || submissionError) && (
                <div className="space-y-4">
                  {routeError && (
                    <div className="p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive-foreground whitespace-pre-line">
                      {routeError}
                    </div>
                  )}

                  {formErrors.length > 0 && (
                    <div className="p-4 bg-destructive/10 border border-destructive rounded-lg">
                      <h3 className="text-destructive-foreground font-medium mb-2">Please fix the following errors:</h3>
                      <ul className="list-disc list-inside text-destructive-foreground space-y-1">
                        {formErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {submissionError && (
                    <div className="p-4 bg-destructive/10 border border-destructive rounded-lg">
                      <div className="flex justify-between">
                        <h3 className="text-destructive-foreground font-medium">Submission Error:</h3>
                        <button
                          onClick={() => setSubmissionError(null)}
                          className="text-destructive-foreground hover:text-destructive-foreground/80"
                          type="button"
                        >
                          &times;
                        </button>
                      </div>
                      <p className="text-destructive-foreground mt-1">{submissionError}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Trip Details Section */}
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-border pb-2">
                  <h3 className="font-medium text-foreground">Trip Details</h3>
                  <div className="bg-secondary p-2 rounded-lg border border-border text-center text-xs text-foreground-muted">
                    <p>Service within {maxDistanceKm} km radius.</p>
                  </div>
                </div>

                {/* Route Map */}
                {isGoogleMapsLoaded ? (
                  <GoogleMapComponent
                    center={center}
                    zoom={zoom}
                    pickupCoords={pickupCoords}
                    destinationCoords={destCoords}
                    pickupAddress={pickupValue}
                    destinationAddress={destValue}
                    mapContainerClassName="w-full h-[150px] md:h-[300px] rounded-lg overflow-hidden"
                  />
                ) : (
                  <div className="w-full h-[150px] md:h-[300px] rounded-lg bg-secondary flex items-center justify-center">
                    {internalMapLoadError || externalMapLoadError ? (
                      <p className="text-destructive p-4 text-center">
                        Map loading failed: {(internalMapLoadError || externalMapLoadError)?.message}. <br />
                        Address input and validation will still work.
                      </p>
                    ) : (
                      <p className="text-foreground-muted">Loading map...</p>
                    )}
                  </div>
                )}

                {showAddressHistory && (
                  <AddressHistoryDropdown
                    addressHistory={addressHistory}
                    isLoading={isLoadingHistory}
                    onSelect={handleAddressHistorySelect}
                    className="mb-2"
                  />
                )}

                {/* --- Pickup Location Lookup Control --- */}
                <div className="relative" data-testid="booking-form-pickup-section">
                  <MapPin className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />
                  <div className="flex space-x-2">
                    <div className="relative flex-1">
                      <InputField
                        type="text"
                        placeholder="Pickup Location *"
                        value={pickupValue}
                        onChange={e => handlePickupInputChange(e.target.value)}
                        onFocus={handlePickupFocus} onBlur={() => {
                          commitTypedPickupLocation();
                          if (!isMobile) {
                            setTimeout(() => {
                              setFocusedInput(null);
                              forceClearPickupSuggestions();
                            }, 150);
                          }
                        }}
                        readOnly={isMobile && formMode !== 'readOnly'}
                        inputClassName={`form-input form-input-with-icon-left form-input-with-icon-right ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                        required
                        disabled={formMode === 'readOnly'}
                        iconLeft={<MapPin className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />}
                        data-testid="booking-form-pickup-input"
                      />
                      {pickupCoords && (
                        <button
                          type="button"
                          onClick={() => { /* ... */ }}
                          className="absolute left-3 top-3 text-primary hover:text-primary/80 transition-colors"
                          title="Focus map on pickup location"
                        >
                          <MapPin className="h-5 w-5" />
                        </button>
                      )}
                      {!pickupCoords && (
                        <MapPin className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />
                      )}
                      {pickupValue && (
                        <button
                          type="button"
                          onClick={clearPickupLocation}
                          className="absolute right-3 top-3 text-foreground-muted hover:text-foreground"
                          disabled={formMode === 'readOnly'}
                          style={{ display: formMode === 'readOnly' ? 'none' : 'block' }}
                          data-testid="booking-form-pickup-clear-button"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      )}                      {pickupSuggestions.length > 0 && focusedInput === 'pickup' && !isMobile && (
                        <div className="absolute z-20 w-full bg-background-paper mt-1 rounded-xl shadow-xl border border-border overflow-hidden" data-testid="booking-form-pickup-suggestions-list">
                          {/* Close button in top-right corner */}
                          <button
                            type="button"
                            onClick={() => {
                              forceClearPickupSuggestions();
                              setFocusedInput(null);
                            }}
                            className="absolute top-2 right-2 text-foreground-muted hover:text-foreground bg-background-paper rounded-full p-1.5 shadow-sm z-10 focus:outline-none focus:ring-2 focus:ring-primary"
                            aria-label="Close pickup suggestions"
                          >
                            <X className="h-4 w-4" />
                          </button>

                          <div className="max-h-64 overflow-y-auto py-2">
                            {pickupSuggestions.map((suggestion) => (
                              <div
                                key={suggestion.place_id}
                                className="p-3 mx-2 my-1 hover:bg-accent focus:bg-accent cursor-pointer text-foreground rounded-lg transition-colors flex items-center gap-2"
                                tabIndex={0}
                                onClick={async (e) => {
                                  // Set focused input to null to ensure dropdown doesn't reappear
                                  setFocusedInput(null);

                                  // Clear suggestions immediately to ensure the dropdown closes
                                  forceClearPickupSuggestions();

                                  // Remove focus from the input
                                  const input = (e.target as HTMLElement).closest('.relative')?.querySelector('input');
                                  if (input) (input as HTMLInputElement).blur();

                                  // Update input values
                                  setPickupValue(suggestion.description);
                                  setPickup(suggestion.description);
                                  setPickupInputValue(suggestion.description, false);
                                  onPickupAddressChange?.(suggestion.description);

                                  // Set coordinates using geocoding
                                  try {
                                    const results = await getGeocode({ placeId: suggestion.place_id });
                                    const latLng = await getLatLng(results[0]);
                                    setPickupCoords(latLng);
                                    onPickupCoordsChange?.(latLng);
                                    onCenterChange?.(latLng); // Center map on new pickup
                                  } catch (error) {
                                    logger.error('Error getting coordinates for pickup', error instanceof Error ? error : undefined);
                                    setRouteError("Could not get coordinates for the selected pickup location.");
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                  }
                                }}
                              >
                                <MapPin className="h-4 w-4 text-primary shrink-0" />
                                <span className="truncate">{suggestion.description}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={getCurrentLocation}
                      className={`px-3 py-2 ${formMode === 'readOnly' ? 'bg-secondary cursor-not-allowed' : 'bg-primary hover:bg-primary/90'} text-primary-foreground rounded-lg transition-colors flex items-center space-x-2`}
                      disabled={formMode === 'readOnly'}
                      data-testid="booking-form-current-location-button"
                    >
                      <Crosshair className="h-5 w-5" />

                    </button>
                  </div>
                </div>

                <div className="relative flex items-center space-x-2" data-testid="booking-form-destination-section">
                  <div className="flex-1 relative">
                    <InputField
                      type="text"
                      placeholder="Destination *"
                      value={destValue}
                      onChange={e => handleDestinationInputChange(e.target.value)}
                      onFocus={handleDestinationFocus} onBlur={() => {
                        commitTypedDestination();
                        if (!isMobile) {
                          setTimeout(() => {
                            setFocusedInput(null);
                            forceClearDestSuggestions();
                          }, 150);
                        }
                      }}
                      readOnly={isMobile && formMode !== 'readOnly'}
                      inputClassName={`form-input form-input-with-icon-left form-input-with-icon-right ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                      required
                      disabled={formMode === 'readOnly'}
                      iconLeft={<Navigation className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />}
                      data-testid="booking-form-destination-input"
                    />
                    {destCoords && (
                      <button
                        type="button"
                        onClick={() => {
                          setCenter(destCoords);
                          onCenterChange?.(destCoords);
                          onZoomChange?.(15);
                        }}
                        className="absolute left-3 top-3 text-primary hover:text-primary/80 transition-colors"
                        title="Focus map on destination location"
                      >
                        <Navigation className="h-5 w-5" />
                      </button>
                    )}
                    {!destCoords && (
                      <Navigation className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />
                    )}
                    {destValue && (
                      <button
                        type="button"
                        onClick={clearDestination}
                        className="absolute right-3 top-3 text-foreground-muted hover:text-foreground"
                        disabled={formMode === 'readOnly'}
                        style={{ display: formMode === 'readOnly' ? 'none' : 'block' }}
                        data-testid="booking-form-destination-clear-button"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}                    {destSuggestions.length > 0 && focusedInput === 'destination' && !isMobile && (
                      <div className="absolute z-20 w-full bg-background-paper mt-1 rounded-xl shadow-xl border border-border overflow-hidden" data-testid="booking-form-destination-suggestions-list">
                        {/* Close button in top-right corner */}
                        <button
                          type="button"
                          onClick={() => {
                            forceClearDestSuggestions();
                            setFocusedInput(null);
                          }}
                          className="absolute top-2 right-2 text-foreground-muted hover:text-foreground bg-background-paper rounded-full p-1.5 shadow-sm z-10 focus:outline-none focus:ring-2 focus:ring-primary"
                          aria-label="Close destination suggestions"
                        >
                          <X className="h-4 w-4" />
                        </button>

                        <div className="max-h-64 overflow-y-auto py-2">
                          {destSuggestions.map((suggestion) => (
                            <div
                              key={suggestion.place_id}
                              className="p-3 mx-2 my-1 hover:bg-accent focus:bg-accent cursor-pointer text-foreground rounded-lg transition-colors flex items-center gap-2"
                              tabIndex={0}
                              onClick={async (e) => {
                                // Set focused input to null to ensure dropdown doesn't reappear
                                setFocusedInput(null);

                                // Clear suggestions immediately to ensure the dropdown closes
                                forceClearDestSuggestions();

                                // Remove focus from the input
                                const input = (e.target as HTMLElement).closest('.relative')?.querySelector('input');
                                if (input) (input as HTMLInputElement).blur();

                                // Update input values
                                setDestValue(suggestion.description);
                                setDestination(suggestion.description);
                                setDestInputValue(suggestion.description, false);
                                onDestinationAddressChange?.(suggestion.description);

                                try {
                                  const results = await getGeocode({ placeId: suggestion.place_id });
                                  const latLng = await getLatLng(results[0]);
                                  setDestCoords(latLng);
                                  onDestinationCoordsChange?.(latLng);
                                  onCenterChange?.(latLng);
                                } catch (error) {
                                  logger.error('Error getting coordinates for destination', error instanceof Error ? error : undefined);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.currentTarget.click();
                                }
                              }}
                            >
                              <Navigation className="h-4 w-4 text-primary shrink-0" />
                              <span className="truncate">{suggestion.description}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleSwapLocations}
                    className={`p-2 ${formMode === 'readOnly' ? 'bg-secondary cursor-not-allowed' : 'bg-background-paper hover:bg-accent'} rounded-lg shadow-sm border border-border`}
                    disabled={formMode === 'readOnly' || !(pickup || pickupValue) || !(destination || destValue)}
                    data-testid="booking-form-swap-locations-button"
                  >
                    <SwapVertical className={`h-5 w-5 ${(!(pickup || pickupValue) || !(destination || destValue)) ? 'text-foreground-muted/50' : formMode === 'readOnly' ? 'text-foreground-muted' : 'text-primary'}`} />
                  </button>
                </div>

                {/* Pickup Time Section */}
                <div className="space-y-3 mt-2 bg-secondary/50 p-4 rounded-lg border border-border" data-testid="booking-form-pickup-time-section">
                  <h3 className="font-medium text-primary">Pickup Time</h3>
                  <div className="space-y-3">                    <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 text-foreground">
                      <InputField
                        type="radio"
                        checked={pickupType === 'asap'}
                        onChange={() => setPickupType('asap')}
                        inputClassName="form-radio"
                        disabled={formMode === 'readOnly'}
                        name="pickupType"
                        data-testid="booking-form-pickup-type-asap-radio"
                      />
                      <span className="font-medium">ASAP</span>
                    </label>
                    <label className="flex items-center space-x-2 text-foreground">
                      <InputField
                        type="radio"
                        checked={pickupType === 'scheduled'}
                        onChange={() => setPickupType('scheduled')}
                        inputClassName="form-radio"
                        disabled={formMode === 'readOnly'}
                        name="pickupType"
                        data-testid="booking-form-pickup-type-scheduled-radio"
                      />
                      <span className="font-medium">Schedule for Later</span>
                    </label>
                  </div>

                    {pickupType === 'scheduled' && (<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div className="relative">
                        <InputField
                          type="date"
                          value={pickupDate}
                          min={today}
                          onChange={handleDateChange}
                          inputClassName={`form-input form-input-with-icon-left ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                          disabled={formMode === 'readOnly'}
                          iconLeft={<Calendar className="absolute left-3 top-3 h-5 w-5 text-primary" />}
                          data-testid="booking-form-pickup-date-input"
                        />
                        <div className="mt-1 text-sm font-medium text-primary">
                          {formatDate(pickupDate)}
                        </div>
                      </div>
                      <div className="relative">
                        <InputField
                          type="time"
                          value={pickupTime}
                          onChange={handleTimeChange}
                          inputClassName={`form-input form-input-with-icon-left ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                          required
                          placeholder="Select time"
                          disabled={formMode === 'readOnly'}
                          iconLeft={<Clock className="absolute left-3 top-3 h-5 w-5 text-primary" />}
                          data-testid="booking-form-pickup-time-input"
                        />
                      </div>
                    </div>
                    )}
                    {dateTimeError && (
                      <p className="text-destructive text-sm">{dateTimeError}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Travel Duration Information (when available) */}
              {isValidRoute && estimatedDuration && (
                <div className="form-container bg-background-paper p-4 rounded-lg border border-border space-y-2">
                  <h3 className="font-medium text-foreground">Estimated Travel Duration</h3>
                  <p className="text-sm text-foreground-muted">* Times are estimates and may vary based on traffic and other conditions</p>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-2">
                    <div className="p-3 bg-primary/10 border-2 border-primary rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-primary-foreground">Car</span>
                        <span className="text-primary-foreground">{formatDuration(estimatedDuration.car)}</span>
                      </div>
                      <span className="text-xs text-primary/80">Recommended</span>
                    </div>
                    <div className="p-3 bg-secondary border border-border rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground-muted">Bus</span>
                        <span className="text-foreground-muted">{formatDuration(estimatedDuration.bus)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Passenger Details Section */}
              <div className="space-y-4" data-testid="booking-form-passenger-details-section">
                <h3 className="font-medium text-foreground border-b border-border pb-2">Passenger Details</h3>
                <div className="space-y-4">
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />
                    <InputField
                      type="text"
                      placeholder="Passenger Name *"
                      value={passengerName}
                      onChange={e => setPassengerName(e.target.value)}
                      inputClassName={`form-input form-input-with-icon-left ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                      required
                      disabled={formMode === 'readOnly'}
                      iconLeft={<User className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />}
                      data-testid="booking-form-passenger-name-input"
                    />
                  </div>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />
                    <InputField
                      type="text"
                      placeholder="Company Name (Optional)"
                      value={companyName}
                      onChange={e => setCompanyName(e.target.value)}
                      inputClassName={`form-input form-input-with-icon-left ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                      disabled={formMode === 'readOnly'}
                      iconLeft={<Briefcase className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />}
                      data-testid="booking-form-company-name-input"
                    />
                  </div>
                  {/* Combined row for Country Code and Mobile Number */}                  <div className="flex items-start space-x-2">
                    <CountryDropdown
                      selectedCountry={selectedCountry}
                      onSelect={handleCountrySelect}
                      disabled={formMode === 'readOnly'}
                      data-testid="booking-form-country-dropdown"
                    />
                    <div className="relative flex-1">
                      <InputField
                        type="tel"
                        name="phone"
                        autoComplete="tel"
                        placeholder="Mobile Number *"
                        value={passengerMobile}
                        onChange={(e) => setPassengerMobile(e.target.value)}
                        inputClassName={`form-input form-input-with-icon-left ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                        required
                        disabled={formMode === 'readOnly'}
                        iconLeft={<Phone className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />}
                        data-testid="booking-form-passenger-phone-input"
                      />
                    </div>
                  </div>
                  {/* Email input section */}                  <div className="relative">
                    <InputField
                      type="email"
                      placeholder="Email Address"
                      value={passengerEmail}
                      onChange={handleEmailChange}
                      inputClassName={`form-input form-input-with-icon-left ${emailError ? 'form-input-error' : ''} ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                      disabled={formMode === 'readOnly'}
                      iconLeft={<Mail className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />}
                      error={emailError}
                      data-testid="booking-form-passenger-email-input"
                    />
                  </div>
                </div>

                {/* Additional Options Section */}
                <div className="space-y-4" data-testid="booking-form-additional-options-section">
                  <h3 className="font-medium text-foreground border-b border-border pb-2">Additional Options</h3>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground-muted">Passengers</label>
                      <div className={`flex items-center space-x-3 p-3 rounded-lg border ${formMode === 'readOnly' ? 'bg-secondary border-border' : 'bg-input border-border'}`} data-testid="booking-form-passengers-control">
                        <Users className="h-5 w-5 text-foreground-muted" />
                        <div className="flex items-center justify-between flex-1 text-foreground">
                          <button
                            type="button"
                            onClick={decrementPassengers}
                            className={`p-1 rounded-full ${formMode === 'readOnly' ? 'text-foreground-muted cursor-not-allowed' : 'hover:bg-accent'}`}
                            disabled={formMode === 'readOnly'}
                            data-testid="booking-form-passengers-decrement-button"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="font-medium" data-testid="booking-form-passengers-count">{passengers}</span>
                          <button
                            type="button"
                            onClick={incrementPassengers}
                            className={`p-1 rounded-full ${formMode === 'readOnly' ? 'text-foreground-muted cursor-not-allowed' : 'hover:bg-accent'}`}
                            disabled={formMode === 'readOnly'}
                            data-testid="booking-form-passengers-increment-button"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>                  <div className="space-y-2">
                      <label className="block text-sm font-medium text-foreground-muted">Luggage</label>
                      <div className="relative">
                        <Select
                          value={luggage}
                          onChange={val => handleLuggageChange(Number(val))}
                          options={[
                            { value: 0, label: 'No luggage' },
                            { value: 1, label: 'Small bag' },
                            { value: 2, label: 'Medium luggage' },
                            { value: 3, label: 'Large luggage' },
                          ]}
                          selectClassName={formMode === 'readOnly' ? 'form-input-disabled' : ''}
                          leftIcon={<Briefcase className="form-input-icon-left" />}
                          rightIcon={<ChevronDown />}
                          disabled={formMode === 'readOnly'}
                          data-testid="booking-form-luggage-select"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Comfort Level Selection */}
                  {(serviceConfig.attributes?.showComfortLevels === undefined || Boolean(serviceConfig.attributes.showComfortLevels)) && (
                    <div className="space-y-3">
                      <h3 className="font-medium text-foreground">Comfort Level</h3>
                      <div className="relative">
                        <div className="relative">
                          <Briefcase className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />
                          <Select
                            value={selectedRide}
                            onChange={val => setSelectedRide(val as string)}
                            options={Object.entries(rideOptions).map(([key, ride]) => ({
                              value: key,
                              label: `${ride.name} - ${ride.comfort}`
                            }))}
                            selectClassName={formMode === 'readOnly' ? 'form-input-disabled' : ''}
                            leftIcon={<Briefcase className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />}
                            rightIcon={<ChevronDown />}
                            disabled={formMode === 'readOnly'}
                            data-testid="booking-form-ride-type-select"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment & Communication Preferences Section */}
                <div className="space-y-4" data-testid="booking-form-payment-communication-section">
                  <h3 className="font-medium text-foreground border-b border-border pb-2">Payment & Communication</h3>

                  {/* Payment Method */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground-muted">Payment Method *</label>
                    <div className="relative">
                      <Select
                        value={paymentMethod}
                        onChange={val => setPaymentMethod(val as string)} options={serviceConfig.attributes?.paymentTypes
                          ? (serviceConfig.attributes.paymentTypes as string).split(',').map(type => {
                            const option = type.trim();
                            return {
                              value: option.toLowerCase().replace(/\s+/g, '-'),
                              label: option
                            };
                          })
                          : paymentOptions.map(option => ({
                            value: option.value,
                            label: option.label
                          }))}
                        selectClassName={formMode === 'readOnly' ? 'form-input-disabled' : ''}
                        leftIcon={<CreditCard className="form-input-icon-left" />}
                        rightIcon={<ChevronDown />}
                        required
                        disabled={formMode === 'readOnly'}
                        placeholder="Select payment method"
                        data-testid="booking-form-payment-method-select"
                      />
                    </div>
                  </div>

                  {/* Communication Methods */}
                  <div className="space-y-2" data-testid="booking-form-communication-section">                  <label className="block text-sm font-medium text-foreground-muted">
                    Preferred Communication Methods *
                  </label>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 text-foreground">                    {isCommunicationMethodAvailable('voice') && (
                      <label className="flex items-center space-x-2">
                        <InputField
                          type="checkbox"
                          checked={communicationPrefs.voice}
                          onChange={(e) => handleCommunicationPrefChange('voice', e.target.checked)}
                          inputClassName="form-checkbox"
                          disabled={formMode === 'readOnly'}
                          data-testid="booking-form-communication-voice-checkbox"
                        />
                        <span>Voice Call</span>
                      </label>
                    )}
                      {isCommunicationMethodAvailable('sms') && (
                        <label className="flex items-center space-x-2">
                          <InputField
                            type="checkbox"
                            checked={communicationPrefs.sms}
                            onChange={(e) => handleCommunicationPrefChange('sms', e.target.checked)}
                            inputClassName="form-checkbox"
                            disabled={formMode === 'readOnly'}
                            data-testid="booking-form-communication-sms-checkbox"
                          />
                          <span>SMS</span>
                        </label>
                      )}
                      {isCommunicationMethodAvailable('whatsapp') && (
                        <label className="flex items-center space-x-2">
                          <InputField
                            type="checkbox"
                            checked={communicationPrefs.whatsapp}
                            onChange={(e) => handleCommunicationPrefChange('whatsapp', e.target.checked)}
                            inputClassName="form-checkbox"
                            disabled={formMode === 'readOnly'}
                            data-testid="booking-form-communication-whatsapp-checkbox"
                          />
                          <span>WhatsApp</span>
                        </label>
                      )}
                      {isCommunicationMethodAvailable('email') && (
                        <label className="flex items-center space-x-2">
                          <InputField
                            type="checkbox"
                            checked={communicationPrefs.email}
                            onChange={(e) => handleCommunicationPrefChange('email', e.target.checked)}
                            inputClassName="form-checkbox"
                            disabled={formMode === 'readOnly'}
                            data-testid="booking-form-communication-email-checkbox"
                          />
                          <span>Email</span>
                        </label>
                      )}
                    </div>
                    {communicationError && (
                      <p className="mt-1 text-sm text-destructive" data-testid="booking-form-communication-error">{communicationError}</p>
                    )}
                  </div>

                  {/* Email Copy Option */}
                  <div className="space-y-2">                  <label className="flex items-center space-x-2">
                    <InputField
                      type="checkbox"
                      checked={sendEmailCopy}
                      onChange={(e) => setSendEmailCopy(e.target.checked)}
                      inputClassName="form-checkbox"
                      disabled={formMode === 'readOnly'}
                      data-testid="booking-form-send-email-copy-checkbox"
                    />
                    <span className="text-sm text-foreground-muted">Send me a copy of the booking details</span>
                  </label>{sendEmailCopy && (
                    <div className="relative">
                      <InputField
                        type="email"
                        placeholder="Email Address for Copy"
                        value={emailCopyAddress}
                        onChange={(e) => {
                          setEmailCopyAddress(e.target.value);
                          validateEmailCopy(e.target.value);
                        }}
                        inputClassName={`form-input form-input-with-icon-left ${emailCopyError ? 'form-input-error' : ''} ${formMode === 'readOnly' ? 'form-input-disabled' : ''}`.trim()}
                        disabled={formMode === 'readOnly'}
                        iconLeft={<Mail className="absolute left-3 top-3 h-5 w-5 text-foreground-muted" />}
                        error={emailCopyError}
                        data-testid="booking-form-email-copy-address-input"
                      />
                    </div>
                  )}
                  </div>
                </div>
              </div> {/* Closes Passenger Details Section */}

              {/* Submit Button Section */}
                <div className="pt-4 border-t border-border">


                  <button
                    type={formMode !== 'readOnly' ? 'submit' : 'button'}
                    className={`w-full py-4 rounded-lg font-medium transition-colors ${(formMode === 'readOnly' || isSubmitting)
                        ? 'bg-secondary text-foreground-muted cursor-not-allowed'
                        : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                      }`}
                    disabled={formMode === 'readOnly' || isSubmitting}
                    title={formMode === 'readOnly' ? 'View-only mode' : ''}
                    data-testid="booking-form-submit-button"
                  >                  {formMode === 'edit' ? 'Update Booking' :
                    formMode === 'readOnly' ? 'View Only' : 'Request Ride'}
                  </button>
                </div>

                {/* Map Status Warning */}
                {mapLoadError && (
                  <div className="mt-4 p-4 bg-warning/10 border border-warning rounded-lg">
                    <h3 className="text-warning-foreground font-medium mb-2">Map Issue:</h3>
                    <p className="text-warning-foreground">
                      Google Maps features may be limited or unavailable. {(internalMapLoadError || externalMapLoadError)?.message}
                    </p>
                    <p className="text-warning-foreground mt-2 text-sm">
                      You can still enter addresses manually and complete your booking.
                    </p>
                  </div>
                )}
              </SectionHeader>
            </div> {/* Closes form-container */}
          </form>
        </div>
      </div>      {/* Mobile Address Search Dialog */}
      <AddressSearchDialog
        isOpen={isAddressDialogOpen}
        onClose={handleCloseMobileDialog}
        onSelectAddress={handleMobileAddressSelect}
        title={addressDialogType === 'pickup' ? 'Select Pickup Location' : 'Select Destination'}
        placeholder={addressDialogType === 'pickup' ? 'Search for pickup location...' : 'Search for destination...'}
        initialValue={addressDialogType === 'pickup' ? pickupValue : destValue}
        requestOptions={{
          ...((enableGlobalSearchFromConfig || !selectedCountry?.country) ? {} : { componentRestrictions: { country: selectedCountry.country.toLowerCase() } })
        }}
        isGoogleMapsLoaded={isGoogleMapsLoaded}
      />
    </div>
  );
};

export default BookingForm;
